/**
 * Meta Pixel Tracking Helper
 * 
 * Safe wrapper for Meta (Facebook) Pixel events.
 * - Never throws: if fbq is missing (ad blocker, iOS native, failed load), it silently no-ops.
 * - Never sends PII (email, name, phone, Clerk ID).
 * - iOS Capacitor native shell is excluded (Apple ATT compliance).
 */

import { isNativeApp } from './platform';

// Session storage key for deduplicating Purchase events
const PURCHASE_FIRED_KEY = 'meta_pixel_purchase_fired';

/**
 * Check if Meta Pixel (fbq) is available and we're allowed to use it.
 * Returns false if:
 * - Running in Capacitor native iOS shell (ATT compliance)
 * - fbq is not defined (ad blocker, script failed to load)
 */
const canTrack = () => {
  // Do not track in native iOS/Android app (Apple ATT compliance)
  if (isNativeApp()) {
    return false;
  }
  // Check if fbq is available
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Fire CompleteRegistration event.
 * Called once when a NEW user is created and trial assigned.
 * 
 * @returns {void}
 */
export const trackCompleteRegistration = () => {
  if (!canTrack()) return;
  
  try {
    window.fbq('track', 'CompleteRegistration');
  } catch (e) {
    // Silent fail - tracking errors must never affect app behavior
  }
};

/**
 * Fire StartTrial event.
 * Called once when a NEW user starts their 7-day trial.
 * 
 * @returns {void}
 */
export const trackStartTrial = () => {
  if (!canTrack()) return;
  
  try {
    window.fbq('track', 'StartTrial', {
      value: 0,
      currency: 'USD'
    });
  } catch (e) {
    // Silent fail - tracking errors must never affect app behavior
  }
};

/**
 * Fire both CompleteRegistration and StartTrial for a new user.
 * Convenience method for signup flow.
 * 
 * @returns {void}
 */
export const trackNewUserSignup = () => {
  trackCompleteRegistration();
  trackStartTrial();
};

/**
 * Fire Purchase event after successful checkout.
 * 
 * Guards:
 * - Only fires if verified_session_id is present (valid checkout session)
 * - Only fires if amount_charged > 0 (skip $0 promo/test checkouts)
 * - Only fires once per session ID (prevents duplicate on page reload)
 * 
 * @param {Object} params
 * @param {string|null} params.verified_session_id - Stripe checkout session ID
 * @param {number|null} params.amount_charged - Amount charged in dollars
 * @param {string|null} params.currency - Currency code (e.g., 'usd')
 * @returns {void}
 */
export const trackPurchase = ({ verified_session_id, amount_charged, currency }) => {
  if (!canTrack()) return;
  
  // Guard: Must have a verified session ID
  if (!verified_session_id) return;
  
  // Guard: Must have amount > 0 (skip $0 promo checkouts)
  if (!amount_charged || amount_charged <= 0) return;
  
  // Guard: Check if we already fired Purchase for this session
  try {
    const firedSessions = JSON.parse(sessionStorage.getItem(PURCHASE_FIRED_KEY) || '[]');
    if (firedSessions.includes(verified_session_id)) {
      return; // Already fired for this session
    }
    
    // Fire the event
    window.fbq('track', 'Purchase', {
      value: amount_charged,
      currency: (currency || 'usd').toUpperCase()
    }, {
      eventID: verified_session_id // Dedupe key for Meta
    });
    
    // Mark this session as fired
    firedSessions.push(verified_session_id);
    sessionStorage.setItem(PURCHASE_FIRED_KEY, JSON.stringify(firedSessions));
  } catch (e) {
    // Silent fail - tracking errors must never affect app behavior
  }
};
